import React from 'react';
import { Gauge } from 'lucide-react';
import energyResourceMappingImage from '@/assets/overview/energy_resource_mapping.png';
import { formatNumber } from '@/utils/formatNumber';
import { IconTitle } from '@/components/ui/icon-title';

interface PowerConsumptionCardProps {
  totalConsumption: number | null | undefined;
  acConsumption: number | null | undefined;
  otherConsumption: number | null | undefined;
  solarProduced: number | null | undefined;
  gridImport: number | null | undefined;
  avgTodayPower?: number | null | undefined;
}

// Component for displaying energy metrics on the map
const EnergyMetric: React.FC<{
  label: string;
  value: number | null | undefined;
  unit: string;
  position: string;
  align?: 'left' | 'right';
}> = ({ label, value, unit, position, align = 'left' }) => (
  <div className={`absolute ${position} z-10`}>
    <div className={align === 'right' ? 'text-right' : 'text-left'}>
      <p className="text-[10px] font-medium text-gray-600 tracking-wide">{label}</p>
      <p className="text-[14px] font-bold text-black">
        {formatNumber(value)} <span className="text-[10px] font-medium text-gray-600">{unit}</span>
      </p>
    </div>
  </div>
);


const PowerConsumptionCard: React.FC<PowerConsumptionCardProps> = ({
  totalConsumption,
  acConsumption,
  otherConsumption,
  solarProduced,
  gridImport,
  avgTodayPower = 0,
}) => {

  // Handle null/undefined values for calculations
  const safeValues = {
    solarProduced: solarProduced ?? 0,
    gridImport: gridImport ?? 0,
    acConsumption: acConsumption ?? 0,
    otherConsumption: otherConsumption ?? 0,
    totalConsumption: totalConsumption ?? 0,
  };

  // Calculate dynamic animation speeds based on energy values
  const getAnimationSpeed = (value: number, maxValue: number) => {
    const normalized = Math.max(0.1, Math.min(1, value / maxValue));
    return 0.8 + (normalized * 2.2); // Speed range: 0.8s to 3s
  };

  const maxEnergyValue = Math.max(safeValues.solarProduced, safeValues.gridImport, safeValues.acConsumption, safeValues.otherConsumption, 1);
  const solarSpeed = getAnimationSpeed(safeValues.solarProduced, maxEnergyValue);
  const gridSpeed = getAnimationSpeed(safeValues.gridImport, maxEnergyValue);
  const acSpeed = getAnimationSpeed(safeValues.acConsumption, maxEnergyValue);
  const otherSpeed = getAnimationSpeed(safeValues.otherConsumption, maxEnergyValue);

  // Enhanced animation styles with dynamic control and particle effects
  React.useEffect(() => {
    const styleId = 'enhanced-energy-flow-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes dashMove {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -18; }
        }
        @keyframes dashMoveReverse {
          from { stroke-dashoffset: -18; }
          to { stroke-dashoffset: 0; }
        }
        .minimal-base-line {
          stroke-linecap: round;
          opacity: 0.2;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Update CSS custom properties for dynamic speeds
  React.useEffect(() => {
    const updateAnimationSpeeds = () => {
      document.documentElement.style.setProperty('--solar-speed', `${solarSpeed}s`);
      document.documentElement.style.setProperty('--grid-speed', `${gridSpeed}s`);
      document.documentElement.style.setProperty('--ac-speed', `${acSpeed}s`);
      document.documentElement.style.setProperty('--other-speed', `${otherSpeed}s`);
    };
    updateAnimationSpeeds();
  }, [solarSpeed, gridSpeed, acSpeed, otherSpeed]);

  return (
    <div className="bg-white rounded-lg border border-light-grey p-3 pt-2 h-full flex flex-col">
      {/* Power Consumption Title */}
      <div className="mb-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <IconTitle icon={Gauge} title="Power Consumption" />
            <span className="text-[8px] text-dark-grey bg-gray-100 px-1.5 py-0.5 rounded">Current</span>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="flex items-baseline gap-1 bg-gray-100 px-1.5 py-0.5 rounded">
              <p className="heading-5 text-black">{formatNumber(totalConsumption, 0)}</p>
              <span className="paragraph-12 text-black">kW</span>
            </div>
          </div>
        </div>
      </div>

      {/* Building Image with Energy Metrics */}
      <div className="relative mt-2 -ml-3 -mr-3 -mb-4">
        <div className="relative w-full">
          <img
            src={energyResourceMappingImage}
            alt="Energy Resource Mapping"
            className="w-full h-auto"
            style={{
              objectFit: "contain",
              objectPosition: "left bottom",
            }}
          />
          
          {/* Container for absolute positioned elements */}
          <div className="absolute inset-0">

            {/* Simple Animated Dashed Lines - Clean approach */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 6 }}>
              {/* Solar - energy flowing INTO building (top to bottom) */}
              {solarProduced !== null && solarProduced > 0 && (
                <line
                  x1="32%"
                  y1="3%"
                  x2="32%"
                  y2="36%"
                  stroke="#F2AA32"
                  strokeWidth={3}
                  strokeDasharray="10,8"
                  strokeLinecap="round"
                  opacity={0.8}
                  style={{
                    animation: `dashMove ${solarSpeed}s linear infinite`
                  }}
                />
              )}
              
              {/* Other - energy flowing OUT of building (bottom to top) */}
              {otherConsumption !== null && otherConsumption > 0 && (
                <line
                  x1="56%"
                  y1="60%"
                  x2="56%"
                  y2="3%"
                  stroke="#9CA3AF"
                  strokeWidth={3}
                  strokeDasharray="10,8"
                  strokeLinecap="round"
                  opacity={0.8}
                  style={{
                    animation: `dashMove ${otherSpeed}s linear infinite`
                  }}
                />
              )}
              
              {/* A/C - energy flowing OUT of building (bottom to top) */}
              {acConsumption !== null && acConsumption > 0 && (
                <line
                  x1="80%"
                  y1="53%"
                  x2="80%"
                  y2="3%"
                  stroke="#0E7EE4"
                  strokeWidth={3}
                  strokeDasharray="10,8"
                  strokeLinecap="round"
                  opacity={0.8}
                  style={{
                    animation: `dashMove ${acSpeed}s linear infinite`
                  }}
                />
              )}
              
              {/* Grid - energy flowing INTO building (bottom to top) */}
              {gridImport !== null && gridImport > 0 && (
                <line
                  x1="81%"
                  y1="92%"
                  x2="81%"
                  y2="62%"
                  stroke="#7D3BBF"
                  strokeWidth={3}
                  strokeDasharray="10,8"
                  strokeLinecap="round"
                  opacity={0.8}
                  style={{
                    animation: `dashMove ${gridSpeed}s linear infinite`
                  }}
                />
              )}
            </svg>

            {/* Solar - positioned to the right of line */}
            <EnergyMetric
              label="SOLAR"
              value={solarProduced}
              unit="kW"
              position="left-[34%] top-[2%]"
              align="left"
            />

            {/* Other - positioned to the right of line */}
            <EnergyMetric
              label="OTHER"
              value={otherConsumption}
              unit="kW"
              position="left-[58%] top-[2%]"
              align="left"
            />

            {/* A/C - positioned to the right of line */}
            <EnergyMetric
              label="A/C"
              value={acConsumption}
              unit="kW"
              position="left-[82%] top-[2%]"
              align="left"
            />

            {/* Grid - positioned to the right of line */}
            <EnergyMetric
              label="GRID"
              value={gridImport}
              unit="kW"
              position="left-[83%] bottom-[6%]"
              align="left"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PowerConsumptionCard;